import useShortcut from 'common/utilities/hooks/useShortcut/useShortcut';
import { useEffect,useState } from 'react';

function nextActiveItem(
  currentItem: number, listLength: number, excludeItems?: number[],
): number {
  const nextItem = currentItem + 1;
  if (nextItem > listLength - 1) {
    return nextActiveItem(-1, listLength, excludeItems); // end reached, back to 1
  }
  if ((excludeItems || []).includes(nextItem)) {
    const resolvedItem = nextActiveItem(nextItem, listLength, excludeItems); // disabled, try next
    return (excludeItems || []).includes(resolvedItem) ? currentItem : resolvedItem;
  }
  return nextItem; // valid next item
}

function previousActiveItem(
  currentItem: number, listLength: number, excludeItems?: number[],
): number {
  const prevItem = currentItem - 1;
  if (prevItem < 0) {
    return previousActiveItem(listLength, listLength, excludeItems); // start reached, go to end
  }
  if ((excludeItems || []).includes(prevItem)) {
    const resolvedItem = previousActiveItem(prevItem, listLength, excludeItems); // disabled, try next
    return (excludeItems || []).includes(resolvedItem) ? currentItem : resolvedItem;
  }
  return prevItem; // valid previous item
}

/**
 * Provide an index to browse using up and down arrows.
 * By default nothing is selected. The user must use the arrows to see a first selection.
 */
function useUpDownList(listLength: number, disabled: boolean, excludeItems?: number[]) {
  const upTrigger = useShortcut('ArrowUp', true, { disabled, debounceTime: 0 });
  const downTrigger = useShortcut('ArrowDown', true, { disabled, debounceTime: 0 });

  const [active, setActive] = useState<number>(-1);
  const [lastListLength, setlastListLength] = useState<number>(listLength);

  const reset = () => setActive(-1);

  useEffect(() => {
    if (downTrigger) {
      setActive((prevState) => nextActiveItem(prevState, listLength, excludeItems));
    }
  }, [downTrigger, listLength, excludeItems]);

  useEffect(() => {
    if (upTrigger) {
      setActive((prevState) => previousActiveItem(prevState, listLength, excludeItems));
    }
  }, [upTrigger, listLength, excludeItems]);

  useEffect(() => {
    setActive((prevState) => {
      let targetItem: number = prevState;
      if (lastListLength !== listLength) {
        setlastListLength(listLength);
        targetItem = -1;
      }

      if (targetItem > listLength - 1) {
        targetItem = -1;
      }

      if ((excludeItems || []).includes(targetItem)) {
        targetItem = nextActiveItem(targetItem, listLength, excludeItems);
      }

      return targetItem;
    });
  }, [lastListLength, listLength, excludeItems]);

  return { active, reset };
}

export default useUpDownList;
